type BuildTestIdParams<T> = {
  testID: T;
  suffix?: string | string[];
};

/**
 * Normalizes a string into lowercase kebab-case, replacing
 * camelCase, dots, underscores, or spaces with hyphens.
 */
const normalizeToKebabCase = (value: string): string => {
  return value
    .replace(/([a-z])([A-Z])/g, '$1-$2') // handle camelCase → camel-case
    .replace(/[\s._]+/g, '-') // replace spaces, dots, underscores → hyphen
    .replace(/-+/g, '-') // avoid multiple consecutive hyphens
    .toLowerCase()
    .trim();
};

/**
 * Builds a consistent testID string from a base ID and optional suffix(es),
 * ensuring the output is kebab-case and properly formatted.
 */
export const buildTestId = <T extends string | undefined>({
  testID,
  suffix,
}: BuildTestIdParams<T>): T extends string ? string : undefined => {
  if (!testID) {
    return undefined as T extends string ? string : undefined;
  }

  const parts: string[] = [normalizeToKebabCase(testID)];

  if (Array.isArray(suffix)) {
    // Warn developers if suffix is empty or contains only one item
    if (suffix.length === 0) {
      console.warn(
        'buildTestId: Received an empty suffix array. Consider omitting the suffix parameter.',
      );
    } else if (suffix.length === 1) {
      console.warn(
        'buildTestId: Received a single-item suffix array. Consider passing the suffix as a string instead of an array.',
      );
    }

    parts.push(...suffix.map(normalizeToKebabCase));
  } else if (typeof suffix === 'string' && suffix.trim() !== '') {
    parts.push(normalizeToKebabCase(suffix));
  }

  return parts.join('-') as T extends string ? string : undefined;
};
